import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Module-level cache survives route navigations.
// In-flight request is shared so simultaneous mounts trigger only one HTTP call.
let cache = null;
let inflight = null;

function readSession() {
  try {
    const raw = sessionStorage.getItem("mkt:agents");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // expire after 5 minutes
    if (!parsed.ts || Date.now() - parsed.ts > 5 * 60 * 1000) return null;
    cache = parsed.agents;
    return parsed.agents;
  } catch (e) {
    // sessionStorage unavailable (private mode / quota / corrupted JSON) — fall back to network fetch.
    if (typeof console !== "undefined") console.warn("[marketplaceStore] readSession failed:", e?.message || e);
    return null;
  }
}

function writeSession(agents) {
  try {
    sessionStorage.setItem("mkt:agents", JSON.stringify({ agents, ts: Date.now() }));
  } catch (e) {
    // Quota or private-mode write blocked — degrade silently to module cache only.
    if (typeof console !== "undefined") console.warn("[marketplaceStore] writeSession failed:", e?.message || e);
  }
}

function getInitial() {
  return cache || readSession() || [];
}

export function invalidateMarketplaceCache() {
  cache = null;
  inflight = null;
  try {
    sessionStorage.removeItem("mkt:agents");
  } catch {
    // ignore
  }
}

export function useMarketplaceAgents() {
  const initial = getInitial();
  const [agents, setAgents] = useState(initial);
  const [loading, setLoading] = useState(initial.length === 0);

  useEffect(() => {
    if (cache && cache.length > 0) return;
    let cancelled = false;
    if (!inflight) {
      inflight = api
        .get("/marketplace")
        .then((r) => {
          cache = r.data.agents || [];
          writeSession(cache);
          return cache;
        })
        .catch(() => {
          inflight = null;
          return [];
        });
    }
    inflight.then((data) => {
      if (cancelled) return;
      setAgents(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { agents, loading };
}
